export interface InfoBlock {
  title: string;
  content: string;
}

export interface NumberedSectionData {
  number: string;
  title: string;
  content: string;
  largeImage?: string;
  smallImages?: string[];
  /** Second paragraph, rendered below the section's media. */
  contentAfter?: string;
  /** Additional paragraph rendered after the section's media. */
  contentAfterExtra?: string;
  /** Follow-up paragraph rendered after `contentAfterExtra`. */
  contentAfterDetailed?: string;
  /** A single grid-sized image rendered after the post-media copy. */
  afterMediaImage?: string;
  /** Copy rendered after the post-media grid image. */
  contentAfterImage?: string;
}

export type ProjectStatus = "Shipped" | "In Progress" | "Contact to view";

export interface Project {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  category: string;
  year: string;
  image?: string;
  heroImage?: string;
  /** Optimized copy of the project's artwork for link previews. */
  socialImage?: string;
  /** Optional hero video; `heroImage` becomes its poster when both are set. */
  heroVideo?: string;
  link?: string;
  featured?: boolean;
  status?: ProjectStatus;
  technologies?: string[];
  accentColor?: string;
  password?: string;
  // New layout fields
  infoBlocks?: InfoBlock[];
  overview?: string;
  sections?: NumberedSectionData[];
  takeaway?: string;
  output?: { value: string; label: string }[];
  quote?: {
    text: string;
    author?: string;
  };
  details?: {
    challenge: string;
    solution: string;
    result: string;
    metrics?: {
      label: string;
      value: string;
    }[];
  };
}

export const projects: Project[] = [
  {
    id: "animalia",
    status: "Shipped",
    title: "Animalia",
    subtitle:
      "A framework-agnostic design system supporting more than 250 products across different technologies.",
    description:
      "Animalia is the design system built for TOTVS — unifying design language across dozens of acquired companies, multiple tech stacks, and hundreds of products into a single, accessible, scalable platform.",
    category: "Design System",
    year: "2021–2025",
    featured: true,
    technologies: [
      "Design Systems",
      "Figma",
      "Documentation",
      "WCAG 2.2",
      "Token Architecture",
      "Icon Library",
    ],
    accentColor: "#2D0456",
    infoBlocks: [
      { title: "Role", content: "Staff Product Designer" },
      { title: "Company", content: "TOTVS" },
      { title: "Platform", content: "Web" },
      { title: "Duration", content: "2021–present" },
    ],
    heroImage: "/images/animalia/intro-poster.jpg",
    socialImage: "/images/social/animalia-2026-09.jpg",
    heroVideo: "/video/animalia-intro.mp4",
    overview:
      "Animalia is TOTVS's framework-agnostic design system, created to unify the experience across more than 250 products. Research revealed that 57% of product teams were working outside the technology supported by the existing system, forcing them to maintain separate component libraries and creating an inconsistent customer experience.\n\nAs Staff Product Designer, I led the design direction from its initial foundations to enterprise adoption. Working with design, product and engineering teams, we built an accessible and flexible system that teams could adopt without changing their technology stack. Animalia now has 86% product adoption, 54 production components and supports more than 120 designers.",
    sections: [
      {
        number: "01",
        title: "Foundation Values",
        content:
          "We started with three components and focused on the login flow—a shared need across almost every product. This gave teams a practical way to adopt the system without committing to a large migration. By Q1 2022, Animalia had expanded to 22 core components, supported by a shared design language and a WCAG AAA accessibility baseline.",
        contentAfter:
          "Everything above is live. Real components, no screenshots — every colour, radius and spacing value resolved from the same token layer the products consume. A few of the controls do more than they look like they do.",
        contentAfterExtra:
          "The component lobby gives every team a shared place to explore the system, understand its vocabulary, and start from patterns that have already been tested in production.",
      },
      {
        number: "02",
        title: "Scale & Innovation",
        content:
          "As adoption increased, teams needed support for more complex workflows. We expanded the system into forms, tasks and tables, introduced motion standards and released the first documentation portal. We also launched the Animalia Icon Library with more than 3,000 icons in regular and filled styles.",
        largeImage: "/images/animalia/calango.png",
        contentAfterExtra:
          "Growth also meant giving teams more room to build on their own — more freedom to design their own components, not just consume the system's. That only worked because of what sat underneath: a shared token layer that became the system's contract.",
        contentAfterDetailed:
          "Behind it, 836 schema variables held the detailed decisions each component needed; designers worked through a curated set of 183 semantic variables, a 78% reduction in the surface they had to navigate day to day.",
        afterMediaImage: "/images/animalia/foundation.png",
        contentAfterImage: "The primitive and component layers stayed protected, and Portuguese-language descriptions in the metadata let designers search by intent and land on the right semantic value without needing to know the complexity underneath. That let teams build new components that stayed consistent across brands, themes and dark mode.",
      },
      {
        number: "03",
        title: "Enterprise Maturity",
        content:
          "Once the system had meaningful adoption, the focus shifted toward governance, maintenance and organizational scale. A company-wide rebrand tested the flexibility of the token architecture. The system absorbed the change in two weeks of design work and one month of engineering rollout. We also upgraded the accessibility baseline to WCAG 2.2 and developed the documentation portal into a self-service resource for designers and developers.",
        largeImage: "/video/vide-sede.mp4",
      },
    ],
    quote: {
      text: "How might we deliver a unified customer experience regardless of the technology each team uses?",
      author: "Guiding hypothesis",
    },
    takeaway:
      "Three things shaped Animalia. First, flexibility comes from abstraction — the system was built to adapt to diverse contexts with consistency, not enforce rigid standards. Second, accessibility was a foundation from day one, not a guideline bolted on later; every component was built to naturally meet diverse user needs. Third, a unified design platform turns M&A from a fragmenting force into a transformation accelerator — letting TOTVS absorb acquisitions without compromising quality or cohesion.",
    output: [
      { value: "86%", label: "adoption across products" },
      { value: "250+", label: "products supported" },
      { value: "120", label: "designers supported" },
      { value: "54", label: "production components" },
      { value: "4.5/5", label: "documentation satisfaction score" },
      { value: "3,000+", label: "icons available" },
    ],
    details: {
      challenge:
        "TOTVS had grown through acquisitions, but its component library was tied to one frontend technology — leaving teams on other stacks with no bridge to the company's design standards. Customers couldn't identify products as coming from the same company.",
      solution:
        "Built a framework-agnostic design system from 0 to 1 with scalable token architecture, comprehensive documentation, WCAG 2.2 compliance, and a 3,000+ icon library — enabling any team on any stack to ship consistent, accessible TOTVS products.",
      result:
        "86% adoption across 250+ products, 173% increase in framework value, 120 designers supported, and login form build time reduced from 2–3 hours to 35 minutes. The system became TOTVS's M&A integration accelerator.",
      metrics: [
        { label: "Adoption", value: "86%" },
        { label: "Designers", value: "120" },
        { label: "Products", value: "+250" },
        { label: "Framework Value", value: "+173%" },
      ],
    },
  },
  {
    id: "academia-da-boa",
    status: "Shipped",
    title: "Academia da Boa",
    subtitle:
      "Transforming bar professional training from physical to digital learning experience",
    description:
      "A mobile-first digital learning platform for bar professionals developed for Antarctica/AMBEV in partnership with Senac, designed to overcome accessibility barriers and increase participation through user-centered design.",
    category: "UX/UI Design",
    year: "2024",
    featured: true,
    technologies: [
      "Material Design",
      "Mobile-first Design",
      "LMS Platform",
      "User Research",
    ],
    accentColor: "#bcdffb",
    infoBlocks: [
      { title: "My Role", content: "Product Designer" },
      { title: "Client", content: "Antarctica/AMBEV & Senac" },
      { title: "Platform", content: "Mobile LMS Platform" },
    ],
    heroImage: "/images/ambev-thumb.png",
    socialImage: "/images/social/academia-da-boa-2026-09.jpg",
    overview:
      "Academia da Boa is going digital! The professional school for bar professionals originally launched in 2017 and  based on a 19th loft in Lapa has accepted the challenge of transitioning to the digital world the physical experience. The project was developed by Antarctica, a Brazilian brewer that has been around since 1885 and was one of the funding companies of the AMBEV consortium 1999. Just like its original version, the digital platform offers exclusive modules to capacitate bar professionals. The project was developed in partnership with Senac.",
    sections: [
      {
        number: "01",
        title: "Understanding the Challenge",
        content:
          "The original physical training program faced significant limitations: participants couldn't travel to headquarters, content wasn't optimized for mobile learning, and lessons were too long for busy professionals. Our target users lived outside city centers, had limited study time, earned less than double minimum wage, and were primarily familiar with Android devices.",
        largeImage: "/images/academia/work-high.gif",
        smallImages: [
          "/images/academia/traning-local.jpeg",
          "/images/academia/academia-da-boa-05.jpg",
        ],
      },
      {
        number: "02",
        title: "Mobile-First Solution Design",
        content:
          "Developed a minimalist LMS platform following Material Design guidelines, featuring an intuitive onboarding experience with navigation tips and video tutorials. The solution prioritized mobile navigation, modular course structure with shorter lessons, and progress tracking with strategic check-in points to accommodate users' limited connectivity and study time.",
        largeImage: "/images/academia/menu_academy.gif",
        smallImages: [
          "/images/academia/academy_onboarding_520.gif",
          "/images/academia/academia-da-boa-04.jpg",
        ],
      },
      {
        number: "03",
        title: "Results and Impact",
        content:
          "The digital transformation achieved remarkable success with a 3.5x increase in participation rates compared to the physical course. This dramatic improvement validated the user-centered design approach and demonstrated the power of addressing real user needs through thoughtful digital solutions.",
        largeImage: "/images/academia/mobile-mocku.jpeg",
        smallImages: [
          "/images/academia/academia-da-boa-09.jpeg",
          "/images/academia/image-7665143.png",
        ],
      },
    ],
    quote: {
      text: "Expand accessibility and engagement by creating an intuitive, familiar platform that reaches underserved audiences and minimizes barriers to participation",
      author: "Project Insight",
    },
    takeaway:
      "This project reinforced the critical importance of user-centered design and iterative testing. By deeply understanding our users' constraints and needs, we created a solution that not only solved accessibility problems but dramatically improved engagement. The 3.5x participation increase proves that thoughtful design can remove barriers and create meaningful impact.",
    output: [
      { value: "3.5×", label: "increase in participation" },
      { value: "+1,750", label: "people impacted" },
    ],
    details: {
      challenge:
        "Transform traditional physical bar training into an accessible digital platform for professionals with limited time, resources, and technical experience, while maintaining educational quality and engagement.",
      solution:
        "Designed a mobile-first LMS platform using Material Design principles, featuring modular content structure, intuitive onboarding, progress tracking, and optimized navigation for users with limited connectivity and Android familiarity.",
      result:
        "Achieved a 3.5x increase in participation rates while successfully reaching previously underserved professionals who couldn't attend physical training sessions.",
      metrics: [
        { label: "Participation Increase", value: "3.5x higher" },
        { label: "Platform Focus", value: "Mobile-first" },
        { label: "Design System", value: "Material Design" },
      ],
    },
  },
  {
    id: "crossx",
    status: "Shipped",
    title: "Cross X",
    subtitle:
      "Revolutionizing Crossfit class scheduling through user-centered design",
    description:
      "A comprehensive mobile app design for CrossFit class scheduling and achievement tracking, featuring machine learning-based check-ins and goal-setting capabilities to enhance the fitness experience.",
    category: "Mobile App Design",
    year: "2024",
    featured: true,
    technologies: [
      "UI/UX Design",
      "User Research",
      "Machine Learning",
      "Prototyping",
      "Affinity Mapping",
    ],
    accentColor: "#093f71",
    infoBlocks: [
      { title: "My Role", content: "UX/UI Designer" },
      { title: "Focus", content: "User Research & Prototyping" },
      { title: "Platform", content: "Mobile App" },
      { title: "Method", content: "User-Centered Design" },
    ],
    heroImage: "/images/cross/ytyttyt-p-2600-p2-1600.jpeg",
    socialImage: "/images/social/crossx-2026-09.jpg",
    overview:
      "Cross X emerged from deep user research within CrossFit communities, addressing the critical gap between fitness goals and class organization. Through comprehensive user surveys and behavioral observation, we identified that users rarely scheduled classes due to complex routines and lack of organizational tools. The solution prioritizes user-centric design with intelligent features that adapt to individual fitness journeys.",
    sections: [
      {
        number: "01",
        title: "User Research & Discovery",
        content:
          "Conducted extensive digital surveys with CrossFit studio students and observed class dynamics to understand user pain points. The research revealed that users struggled with class scheduling and lacked organizational tools to track their fitness progress. We developed detailed personas representing both athlete and non-athlete user types to guide our design decisions.",
        largeImage: "/images/cross/crossx_emphaty-p-1080 copy.jpg",
        smallImages: [
          "/images/cross-thumb.png",
          "/images/cross/eduardo-cano-photo-co-cN2iO_j5NhY-unsplash.jpg",
        ],
      },
      {
        number: "02",
        title: "Design Solutions & Features",
        content:
          "Created a streamlined dashboard with enhanced visibility and implemented machine learning-based check-in systems. The design features intelligent notifications, goal-setting capabilities, and achievement tracking bars. We used MoSCoW prioritization method to ensure the most critical features were developed first, focusing on simplifying navigation and improving user engagement.",
        largeImage: "/images/cross/asasa-p-2600-p-1080.jpeg",
        smallImages: [
          "/images/cross/Frame 5_1.png",
          "/images/cross/checkin.gif",
        ],
      },
      {
        number: "03",
        title: "Impact & Learning",
        content:
          "The project reinforced the essential nature of continuous research throughout the design process. By prioritizing user feedback and iterative testing, we created a solution that not only addressed immediate scheduling needs but also enhanced the overall CrossFit experience through intelligent features and intuitive design.",
        largeImage: "/images/cross/iPhone 13 Pro Mockup Light.png",
      },
    ],
    quote: {
      text: "Develop a comprehensive solution that optimizes spatial efficiency, ensures precise data handling, and delivers an intuitive user experience.",
      author: "Designer Reflection",
    },
    takeaway:
      "This project demonstrated the power of thorough user research in uncovering hidden pain points and designing solutions that truly serve user needs. The combination of behavioral observation, survey data, and persona development created a foundation for design decisions that resulted in a more intuitive and effective fitness app experience.",
    details: {
      challenge:
        "CrossFit users rarely scheduled classes due to complex routines and lack of organizational tools, creating barriers to consistent fitness engagement and goal achievement.",
      solution:
        "Designed a comprehensive mobile app featuring machine learning-based check-ins, intelligent notifications, goal-setting capabilities, and streamlined navigation using user-centered design principles and MoSCoW prioritization.",
      result:
        "Created an intuitive fitness app that simplifies class scheduling and enhances user engagement through personalized features and achievement tracking, validated through continuous user research and iterative design.",
      metrics: [
        { label: "Research Method", value: "User Surveys" },
        { label: "Personas Created", value: "2 Types" },
        { label: "Design Approach", value: "User-Centered" },
      ],
    },
  },
  {
    id: "barista",
    status: "Shipped",
    title: "ba.ris.ta",
    subtitle:
      "An iOS app for brewing coffee — built solo, available in the App Store.",
    description:
      "An iOS app for brewing coffee — built solo, available in the App Store.",
    category: "Mobile App",
    year: "2025",
    featured: true,
    technologies: [
      "SwiftUI",
      "Supabase",
      "Cursor",
      "Live Activities",
      "Dynamic Island",
    ],
    link: "https://apps.apple.com/br/app/ba-ris-ta/id6748932374?l=en-GB", // Apple Store link placeholder
    accentColor: "#d6d3d1",
    infoBlocks: [
      { title: "My Role", content: "Designer Engineer" },
      { title: "Duration", content: "4 months" },
      { title: "Stack", content: "SwiftUI, Supabase, Cursor" },
      { title: "Platform", content: "iOS" },
    ],
    heroImage: "/images/barista/hero.png",
    socialImage: "/images/social/barista-2026-09.jpg",
    overview:
      "I built this because the alternatives didn't fit the ritual. Making a good cup of coffee is unhurried and precise. The tools around it — scattered across Notes, Clock, and whatever recipe app I'd last downloaded — weren't. The app I wanted was minimal, native, and smart about staying out of the way. So I built it.",
    sections: [
      {
        number: "01",
        title: "On going fully native",
        content:
          "The most important early decision was committing to Dynamic Island and Live Activities. It sounds like a feature; it was really a philosophy. If the app required you to look at it, it wasn't solving the problem. Live brew timers on the lock screen. Method and ratio visible in the Island during an active session. The phone goes down; the coffee gets made. SwiftUI made this achievable without compromise.",
        largeImage: "/images/barista/screens.png",
        smallImages: [
          "/images/barista/live-actives-1.png",
          "/images/barista/live-small-2.png",
        ],
      },
      {
        number: "02",
        title: "On building with AI",
        content:
          "I used Claude and Cursor throughout — not as a shortcut, but as a way to close the gap between design intent and implementation. Descriptions became working code. Iterations happened inside a single session rather than across days. The creative judgment was mine; the tools removed the translation cost. What I didn't expect: AI-assisted development forced me to articulate decisions more clearly. You can't be vague with a prompt.",
        largeImage: "/images/barista/ded-1.png",
      },
      {
        number: "03",
        title: "On listening to users",
        content:
          "TestFlight changed the product. Recipe navigation went through three redesigns based on what early users actually did — not what I expected them to do. The journaling feature I thought would anchor the experience turned out to matter less than frictionless recipe access. Shipping early was the right call. I should have done it sooner.",
        largeImage: "/images/barista/app-store.png",
      },
    ],
    quote: {
      text: "Build the coffee brewing tool that didn't exist — minimal, native, and designed to disappear into the ritual rather than interrupt it.",
      author: "The Developer",
    },
    takeaway:
      "The best tools get out of the way. Whether it was committing to Live Activities so the phone could go face-down, shipping early enough to let real users reshape the product, or using AI to close the gap between intention and execution — every meaningful decision was about reducing friction, not adding features. Build for the ritual, not the résumé.",
    output: [
      { value: "+80", label: "user" },
      { value: "+200", label: "brewing sessions" },
      { value: "4.5", label: "store rating" },
    ],
    details: {
      challenge:
        "The tools around brewing coffee — scattered across Notes, Clock, and recipe apps — didn't fit the ritual. Making a good cup of coffee is unhurried and precise; the digital experience around it wasn't.",
      solution:
        "Built a minimal, native iOS app committed to staying out of the way. Dynamic Island and Live Activities meant the phone goes down while the coffee gets made. AI-assisted development with Claude and Cursor closed the gap between design intent and implementation.",
      result:
        "An App Store app, built solo, in four months. Minimal, opinionated, and still the first thing I open every morning.",
      metrics: [
        { label: "Development Time", value: "4 months" },
        { label: "Platform", value: "iOS" },
        { label: "Stack", value: "SwiftUI" },
      ],
    },
  },
];

export const getProjectById = (id: string): Project | undefined => {
  return projects.find((project) => project.id === id);
};

export const getFeaturedProjects = (): Project[] => {
  return projects.filter((project) => project.featured);
};

export const getProjectsByCategory = (category: string): Project[] => {
  return projects.filter((project) => project.category === category);
};

export const getValidSections = (
  sections?: NumberedSectionData[],
): NumberedSectionData[] => {
  if (!sections) return [];

  return sections.filter((section) => {
    const hasValidLargeImage =
      section.largeImage &&
      (section.largeImage.startsWith("/") ||
        section.largeImage.startsWith("http"));

    const hasValidSmallImages =
      section.smallImages &&
      section.smallImages.length > 0 &&
      section.smallImages.every(
        (img) =>
          typeof img === "string" &&
          (img.startsWith("/") || img.startsWith("http")),
      );

    return hasValidLargeImage || hasValidSmallImages;
  });
};
